import { BusinessPlanStatus } from '../../businessplan.types';

/**
 * @mixin
 * @description Esse mixins pelas ações de versionamento do BP, que aparecem no rodapé da página. 
 * @requires {@link BusinessPlanCore} requer o mixin base para o `businessPlanId` e o `formData`.
 * @example
 * mixins: [BusinessPlanVersioning]
 */
export default {
  computed: {
    /**
     * Desativa a seleção de versões caso não haja versões ou apenas a Versão Oficial
     * @type {boolean}
     * @readonly
     */
    disableVersionSelect() {
      const { versoes } = this.formData ?? {};
      return Array.isArray(versoes) && versoes.length < 2;
    },
    /**
     * Retorna a versão selecionada
     * @type {import('../../businessplan.types').Versao}
     * @readonly
     */
    selectedVersion() {
      let { versoes } = this.formData ?? {};

      if (!Array.isArray(versoes)) {
        versoes = [];
      }

      const businessPlanId = typeof this.businessPlanId === 'number' ? this.businessPlanId : parseInt(this.businessPlanId, 10);
      const selectedVersion = (versoes.find(({ id }) => businessPlanId === id)) || {};
      let latestVersion = 0;

      versoes.forEach(({ versao }) => {
        if (latestVersion < versao) {
          latestVersion = versao;
        }
      });

      return { ...selectedVersion, isLast: selectedVersion.versao === latestVersion };
    },
    /**
     * Exibe a seleção de versionamento caso o BP já tenha sido aprovado e esteja 'Em andamento'
     * @type {boolean}
     * @readonly
     */
    showVersioning() {
      return !!this.businessPlanId && [
        BusinessPlanStatus.ANDAMENTO,
        BusinessPlanStatus.PRE_AMORTIZACAO,
        BusinessPlanStatus.AMORTIZACAO,
        BusinessPlanStatus.PREENCHIMENTO_NOVA_VERSAO,
        BusinessPlanStatus.APROVACAO_NOVA_VERSAO,
      ].includes(this.formData?.status);
    },
    // Testando o label para funcionar para o cliente XP Inc. Não pensado para outros clientes.
    /**
     * Exibe os botões de versionamento caso o BP já tenha sido aprovado e esteja 'Em andamento'
     * @type {boolean}
     * @readonly
     */
    showVersioningActions() {
      return this.showVersioning && (this.hasAdminRole || this.isDemandant);
    }
  },
  methods: {
    /**
     * Aprova a versão selecionada (sendo exibida no momento), tornando-a Versão Oficial.
     */
    async approveVersion() {
      try {
        const resource = this.apiResource(
          `/v1/ativacaoped/${this.clientId}/business-plans/${this.businessPlanId}/aprovar-versao`
        );
        await resource.save({});

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Versão aprovada",
          text: "Está se tornou a versão oficial do Business Plan."
        });

        this.doLoad();
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    /**
     * Cria uma nova versão a partir da versão selecionada, para alteração e fluxo de aprovação.
     * Usado apenas na Versão Oficial.
     */
    async createVersion() {
      try {
        const resource = this.apiResource(
          `/v1/ativacaoped/${this.clientId}/business-plans/${this.businessPlanId}/versao`
        );
        const response = await resource.save({});

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Nova versão criada com sucesso",
          text: "Redirecionando para a nova versão."
        });

        this.goToVersion(response.data.id);
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    /**
     * Redireciona via Vue-router para a mesma página, mas com os dados da versão selecionada.
     * @param {number} id identificador único da versão.
     */
    goToVersion(id) {
      this.$router.push({
        name: "ativacaoped-projetos-form-edicao",
        params: { id }
      });
    },
    /**
     * Reprova a versão selecionada, redirecionando o usuário para a Versão Oficial.
     */
    async reproveVersion() {
      try {
        const resource = this.apiResource(
          `/v1/ativacaoped/${this.clientId}/business-plans/${this.businessPlanId}/reprovar-versao`
        );
        await resource.save({});

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Versão reprovada",
          text: "A versão foi apagada e não está mais disponível"
        });

        const { versoes } = this.formData ?? {};
        const officialVersion =
          Array.isArray(versoes) &&
          versoes.find(({ versao_oficial }) => versao_oficial);
        if (officialVersion) {
          this.goToVersion(officialVersion.id);
        } else {
          this.$router.push({
            name: "ativacaoped-projetos"
          });
        }
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    /**
     * 
     */
    async sendVersionToRevision() {
      try {
        const url = `/v1/ativacaoped/${this.clientId}/business-plans/${this.businessPlanId}/notificar-nova-versao`;
        const resource = this.apiResource(url);
        await resource.save({});

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Versão enviada para aprovação",
          text: "Um FP&A irá avaliar antes de torna-la a versão oficial."
        });

        this.doLoad();
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    }
  }
};
